import { Navigate, Route, Routes } from "react-router-dom";
import { OrgProvider, useOrg } from "./lib/org";
import { ApiRequestError } from "./lib/api";
import { Layout } from "./components/Layout";
import { Spinner, ErrorState } from "./components/ui";
import Login from "./pages/Login";
import AdminUsers from "./pages/admin/Users";
import ResetTestBooks from './pages/admin/ResetTestBooks';
import BankReview from './pages/BankReview';
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import Customers from "./pages/Customers";
import Vendors from "./pages/Vendors";
import Items from "./pages/Items";
import Invoices from "./pages/Invoices";
import InvoiceForm from "./pages/InvoiceForm";
import InvoiceDetail from "./pages/InvoiceDetail";
import Bills from "./pages/Bills";
import BillForm from "./pages/BillForm";
import Payments from "./pages/Payments";
import Expenses from "./pages/Expenses";
import ExpenseForm from "./pages/ExpenseForm";
import ExpenseDetail from "./pages/ExpenseDetail";
import JournalEntries from "./pages/JournalEntries";
import ReportsIndex from "./pages/reports/ReportsIndex";
import ReportingCenter from './pages/reports/ReportingCenter';
import ProfitAndLoss from "./pages/reports/ProfitAndLoss";
import BalanceSheet from "./pages/reports/BalanceSheet";
import TrialBalance from "./pages/reports/TrialBalance";
import { ARaging, APaging } from "./pages/reports/Aging";
import GeneralLedger from "./pages/reports/GeneralLedger";
import Settings from "./pages/Settings";
import Corrections from './pages/Corrections';
import Credits from './pages/Credits';
import { lazy, Suspense } from "react";
const Prototype = import.meta.env.DEV ? lazy(() => import("./prototype/Prototype")) : null;
const ManualInvoicePilot = lazy(() => import('./pages/ManualInvoicePilot'));

function Shell() {
  const { loading, error, orgs, user, refetch, currentOrg } = useOrg();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading your books…" />
      </div>
    );
  }
  // Unauthenticated → show the login / register screen.
  if (error instanceof ApiRequestError && error.status === 401) {
    return <Login onSuccess={() => refetch()} />;
  }
  if (error) {
    return (
      <div className="mx-auto mt-20 max-w-md">
        <ErrorState error={error} />
      </div>
    );
  }
  if (orgs.length === 0) {
    return <Onboarding />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/accounts" element={<ChartOfAccounts />} />
        <Route path="/bank-review" element={<BankReview />} />
        <Route path="/corrections" element={<Corrections />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/items" element={<Items />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoice-drafts" element={<Suspense fallback={<Spinner />}><ManualInvoicePilot /></Suspense>} />
        <Route path="/invoices/new" element={<InvoiceForm />} />
        <Route path="/invoices/:id" element={<InvoiceDetail />} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/bills/new" element={<BillForm />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/expenses/new" element={<ExpenseForm />} />
        <Route path="/expenses/:id" element={<ExpenseDetail />} />
        <Route path="/journal" element={<JournalEntries />} />
        <Route path="/reports" element={<ReportsIndex />} />
        <Route path="/reports/cash-flow" element={<ReportingCenter cashFlowOnly />} />
        <Route path="/reports/workspace" element={<ReportingCenter />} />
        <Route path="/reports/profit-and-loss" element={<ProfitAndLoss />} />
        <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
        <Route path="/reports/trial-balance" element={<TrialBalance />} />
        <Route path="/reports/ar-aging" element={<ARaging />} />
        <Route path="/reports/ap-aging" element={<APaging />} />
        <Route path="/reports/general-ledger" element={<GeneralLedger />} />
        <Route path="/settings" element={<Settings />} />
        {user?.isSuperAdmin && <Route path="/admin/reset-test-books" element={<ResetTestBooks key={currentOrg?.id} />} />}
        {user?.isSuperAdmin && (
          <Route path="/admin/users" element={<AdminUsers />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  if (Prototype && /^\/prototype(?:\/|$)/.test(window.location.pathname)) {
    return <Suspense fallback={<Spinner />}><Routes><Route path="/prototype/*" element={<Prototype />} /></Routes></Suspense>;
  }
  return (
    <OrgProvider>
      <Shell />
    </OrgProvider>
  );
}
